import { cn } from '@shadcn/lib/utils';
import { Table as ShadcnTable } from '@shadcn/ui/table';
import {
  ColumnDef,
  ExpandedState,
  TableOptions,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { TABLE_CONSTANTS } from './constants';
import { TablePagination } from './table-subcomponents/pagination/table-pagination';
import { TableBody } from './table-subcomponents/table-body';
import { TableHeader } from './table-subcomponents/table-header';
import { createTableInitialState, createTableState } from './table-utils';
import { TableManipulationOptions } from './types';

function useExpandedRowsState<TData>(
  data: TData[],
  isServerSide: boolean,
  getRowId?: (row: TData, index: number) => string
) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const hasStableRowIds = Boolean(getRowId);
  const resetOnDataChange = isServerSide && !hasStableRowIds;

  useEffect(() => {
    if (resetOnDataChange) {
      setExpanded({});
    }
  }, [data, resetOnDataChange]);

  return [expanded, setExpanded] as const;
}

function createExpanderColumn<TData>(): ColumnDef<TData> {
  return {
    cell: ({ row }) => (
      <button
        aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
        className="flex items-center justify-center cursor-pointer"
        onClick={row.getToggleExpandedHandler()}
        type="button"
      >
        {row.getIsExpanded() ? <ChevronDown className="h-6 w-6" /> : <ChevronRight className="h-6 w-6" />}
      </button>
    ),
    enableSorting: false,
    header: () => null,
    id: TABLE_CONSTANTS.EXPANDER_COLUMN_ID,
  };
}

interface ITableConfigOptions<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  manipulationOptions?: TableManipulationOptions;
  pageSize?: number | null;
}

function createTableConfig<TData>({
  columns,
  data,
  manipulationOptions,
  pageSize,
}: ITableConfigOptions<TData>): TableOptions<TData> {
  const isServerSide = manipulationOptions?.type === 'serverSide';
  const isClientSide = manipulationOptions?.type === 'clientSide';

  return {
    autoResetPageIndex: isClientSide,
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: isClientSide ? getFilteredRowModel() : undefined,
    getPaginationRowModel: isClientSide ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: isClientSide ? 'includesString' : undefined,
    initialState: createTableInitialState(manipulationOptions, pageSize ?? undefined),
    manualPagination: isServerSide,
    manualSorting: isServerSide,
    ...(isServerSide ? { onPaginationChange: manipulationOptions.onPaginationChange } : {}),
    ...(isServerSide ? { onSortingChange: manipulationOptions.onSortingChange } : {}),
    rowCount: isServerSide ? manipulationOptions.totalRowCount : undefined,
    state: createTableState(manipulationOptions),
  };
}

interface ITableProps<TData> {
  readonly className?: string;
  readonly columns: ColumnDef<TData>[];
  readonly data: TData[];
  readonly getRowId?: (row: TData, index: number) => string;
  readonly manipulationOptions?: TableManipulationOptions;
  readonly pageSize?: number | null;
  readonly renderSubRow?: (row: TData) => React.ReactNode;
}

const Table = <TData,>({
  className,
  columns,
  data,
  getRowId,
  manipulationOptions,
  pageSize,
  renderSubRow,
}: ITableProps<TData>) => {
  const isServerSide = manipulationOptions?.type === 'serverSide';
  const [expanded, setExpanded] = useExpandedRowsState(data, isServerSide, getRowId);

  const resolvedColumns = useMemo(
    () => (renderSubRow ? [createExpanderColumn<TData>(), ...columns] : columns),
    [columns, renderSubRow]
  );

  const tableConfig = useMemo(
    () =>
      createTableConfig({
        columns: resolvedColumns,
        data,
        manipulationOptions,
        pageSize,
      }),
    [resolvedColumns, data, manipulationOptions, pageSize]
  );
  const table = useReactTable({
    ...tableConfig,
    getExpandedRowModel: renderSubRow ? getExpandedRowModel() : undefined,
    getRowCanExpand: renderSubRow ? () => true : undefined,
    getRowId,
    onExpandedChange: renderSubRow ? setExpanded : undefined,
    state: { ...tableConfig.state, expanded },
  });

  const { pageSize: currentPageSize } = table.getState().pagination;

  const pageCount = isServerSide
    ? Math.max(1, Math.ceil(manipulationOptions.totalRowCount / currentPageSize))
    : table.getPageCount();

  const loading = isServerSide && manipulationOptions.loading;

  return (
    <div className={cn(TABLE_CONSTANTS.STYLING.BORDERS.TABLE, className)}>
      <ShadcnTable className="shadow-md table-fixed">
        <TableHeader headerGroups={table.getHeaderGroups()} />
        <TableBody
          columns={resolvedColumns}
          loading={loading}
          renderSubRow={renderSubRow}
          rows={table.getRowModel().rows}
        />
      </ShadcnTable>
      {pageSize !== null && (
        <TablePagination
          currentPageSize={currentPageSize}
          manipulationOptions={isServerSide ? manipulationOptions : undefined}
          pageCount={pageCount}
          table={table}
        />
      )}
    </div>
  );
};
export type { ITableProps };
export { Table };
